import {alias, createModelSchema, identifier, reference} from "serializr";
import {Cloneable} from "@/common/Cloneable";
import {Schema} from "@/common/Schema";

export class SmartOfferBundleKind extends Cloneable {
    static NONE = new SmartOfferBundleKind(0, "NONE");
    static SHOP = new SmartOfferBundleKind(1, "SHOP");
    static FIREFLY = new SmartOfferBundleKind(2, "FIREFLY");
    static ACHIEVEMENT = new SmartOfferBundleKind(3, "ACHIEVEMENT");
    static MARKET = new SmartOfferBundleKind(4, "MARKET");
    static CASCADE = new SmartOfferBundleKind(5, "CASCADE");
    static TREASURE = new SmartOfferBundleKind(6, "TREASURE");
    static ENERGY_REFILL = new SmartOfferBundleKind(7, "ENERGY_REFILL");
    static PROGRESSIVE = new SmartOfferBundleKind(8, "PROGRESSIVE");
    static MASTER_SEASON = new SmartOfferBundleKind(9, "MASTER_SEASON");
    static SUNKEN_RUINS = new SmartOfferBundleKind(10, "SUNKEN_RUINS");
    static ANCIENT_TREASURE = new SmartOfferBundleKind(11, "ANCIENT_TREASURE");
    static COLLECTION = new SmartOfferBundleKind(12, "COLLECTION");
    static DAILY_PASS = new SmartOfferBundleKind(13, "DAILY_PASS");
    static DECORATION = new SmartOfferBundleKind(14, "DECORATION");
    static HIDDEN_TEMPLE = new SmartOfferBundleKind(15, "HIDDEN_TEMPLE");
    static ALCHEMY = new SmartOfferBundleKind(16, "ALCHEMY");
    static TIME_LIMITED = new SmartOfferBundleKind(17, "TIME_LIMITED");
    static ANY_PURCHASE = new SmartOfferBundleKind(18, "ANY_PURCHASE");
    static MULTI_TAB = new SmartOfferBundleKind(19, "MULTI_TAB");
    static TREASURE_ACHIEVEMENT = new SmartOfferBundleKind(20, "TREASURE_ACHIEVEMENT");
    static PLINKO = new SmartOfferBundleKind(21, "PLINKO");
    static MAGIC_SPIN = new SmartOfferBundleKind(22, "MAGIC_SPIN");
    static TREASURE_SHOP = new SmartOfferBundleKind(23, "TREASURE_SHOP");
    static TREASURE_OFFER = new SmartOfferBundleKind(24, "TREASURE_OFFER");
    static PLINKO_SHOP_SIMPLE = new SmartOfferBundleKind(25, "PLINKO_SHOP_SIMPLE");
    static PLINKO_SHOP_DOUBLE = new SmartOfferBundleKind(26, "PLINKO_SHOP_DOUBLE");
    static ONE_PLUS_TWO = new SmartOfferBundleKind(27, "ONE_PLUS_TWO");
    static CARD_BOX_BANK = new SmartOfferBundleKind(28, "CARD_BOX_BANK");
    static EXPEDITION_TREASURE_GATE = new SmartOfferBundleKind(29, "EXPEDITION_TREASURE_GATE");
    static EXPEDITION_TREASURE_TREE = new SmartOfferBundleKind(30, "EXPEDITION_TREASURE_TREE");
    static NOVICE_BATTLE_PASS = new SmartOfferBundleKind(31, "NOVICE_BATTLE_PASS");
    static PIGGY = new SmartOfferBundleKind(32, "PIGGY_BANK");
    static REWARD_GIVER = new SmartOfferBundleKind(33, "REWARD_GIVER");
    static CHRISTMAS_HUB = new SmartOfferBundleKind(34, "CHRISTMAS_HUB");
    static CHRISTMAS_CALENDAR = new SmartOfferBundleKind(35, "CHRISTMAS_CALENDAR");
    static CHRISTMAS = new SmartOfferBundleKind(36, "CHRISTMAS");
    static EXPEDITION_GROUP = new SmartOfferBundleKind(37, "EXPEDITION_GROUP");
    static FLASH_AD = new SmartOfferBundleKind(38, "FLASH_AD");
    static MAGICAL_CINEMA = new SmartOfferBundleKind(39, "MAGICAL_CINEMA");
    static HUB = new SmartOfferBundleKind(40, "HUB");

    static _values = [
        this.SHOP,
        this.MARKET,
        this.CASCADE,
        this.FIREFLY,
        this.FLASH_AD,
        this.TREASURE,
        this.PROGRESSIVE,
        this.ACHIEVEMENT,
        this.MASTER_SEASON,
        this.SUNKEN_RUINS,
        this.ANCIENT_TREASURE,
        this.COLLECTION,
        this.DAILY_PASS,
        this.DECORATION,
        this.HIDDEN_TEMPLE,
        this.ALCHEMY,
        this.TIME_LIMITED,
        this.ANY_PURCHASE,
        this.MULTI_TAB,
        this.TREASURE_ACHIEVEMENT,
        this.PLINKO,
        this.PLINKO_SHOP_SIMPLE,
        this.PLINKO_SHOP_DOUBLE,
        this.MAGIC_SPIN,
        this.TREASURE_SHOP,
        this.TREASURE_OFFER,
        this.ONE_PLUS_TWO,
        this.CARD_BOX_BANK,
        this.EXPEDITION_TREASURE_GATE,
        this.EXPEDITION_TREASURE_TREE,
        this.NOVICE_BATTLE_PASS,
        this.PIGGY,
        this.REWARD_GIVER,
        this.CHRISTMAS_HUB,
        this.CHRISTMAS_CALENDAR,
        this.CHRISTMAS,
        this.EXPEDITION_GROUP,
        this.ENERGY_REFILL,
        this.MAGICAL_CINEMA,
        this.HUB
    ];

    static schema() {
        return reference("id", (id, cb) => {
            return cb(null, SmartOfferBundleKind.getValue(id));
        });
    }

    static is(value) {
        if (value instanceof SmartOfferBundleKind) {
            return SmartOfferBundleKind._values.some(v => v.id === value.id);
        } else {
            return false;
        }
    }

    static isNone(value) {
        return SmartOfferBundleKind.NONE.equal(value);
    }

    constructor(id = 0, name = "DEFAULT") {
        super();
        this._id = id;
        this._name = name;
    }

    get id() {
        return this._id;
    }

    set id(value) {
        this._id = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    static getValues() {
        return this._values;
    }

    static getValue(id) {
        let value = this._values.find(v => v.id === id) || null;
        console.assert(value, "unknown id: " + id);
        return value;
    }

    equal(value) {
        if (value instanceof SmartOfferBundleKind) {
            return this.id === value.id;
        } else if (Number.isInteger(value)) {
            return this.id === value;
        }
        return false;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new SmartOfferBundleKind());
        } else if (target instanceof SmartOfferBundleKind) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(SmartOfferBundleKind, {
    _id: alias("id", identifier()),
    _name: alias("name", Schema.primitive())
});
